import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {useForm} from 'react-hook-form'
import {
    Button,
    Flex,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Switch, Radio, RadioGroup} from '@chakra-ui/react'
import WebDrawer from '../web-drawer'
import Field from '../field'

/**
 * Drawer for creating a new collection or renaming an existing one.
 * Pass `folder` prop to enter rename mode; leave it undefined for create mode.
 * In rename mode, a "Remove this collection" button opens an in-place
 * confirmation view with options to keep or remove items.
 */
const WishlistFolderFormModal = ({isOpen, onClose, onSubmit, onDelete, folder, folderItemCount = 0, initialName = ''}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('WishlistFolderFormModal', {})
    const isRename = Boolean(folder)

    // 'edit' | 'confirm'
    const [view, setView] = useState('edit')
    // 'keep' | 'remove'
    const [deleteMode, setDeleteMode] = useState('keep')

    // Visibility toggle — default ON; track initial state so Cancel can revert
    const [isPublic, setIsPublic] = useState(true)
    const [initialIsPublic, setInitialIsPublic] = useState(true)
    const removeCollectionButtonRef = useRef(null)

    const {
        control,
        handleSubmit,
        reset,
        formState: {errors, isSubmitting}
    } = useForm({defaultValues: {name: folder?.name ?? ''}})

    // Reset form, toggle and view whenever the drawer opens
    useEffect(() => {
        if (isOpen) {
            reset({name: folder?.name ?? initialName}, {shouldFocus: false})
            setIsPublic(true)
            setInitialIsPublic(true)
            setView('edit')
            setDeleteMode('keep')
        }
    }, [isOpen])

    const handleFormSubmit = async ({name}) => {
        await onSubmit?.({name: name.trim(), isPublic})
        onClose()
        reset({}, {shouldFocus: false})
        setIsPublic(true)
    }

    // Cancel: revert toggle to its state when the drawer opened, then close
    const handleCancel = () => {
        setIsPublic(initialIsPublic)
        reset({}, {shouldFocus: false})
        onClose()
    }

    const handleConfirmDelete = () => {
        onDelete?.({keepItems: deleteMode === 'keep'})
    }

    const handleCancelConfirm = () => {
        setView('edit')
        // Keep the modal open and move focus away from the name field.
        requestAnimationFrame(() => {
            removeCollectionButtonRef.current?.focus()
        })
    }

    const drawerTitle =
        view === 'confirm'
            ? formatMessage({id: 'wishlist.folder.remove_confirm_title', defaultMessage: 'Remove Collection'})
            : isRename
            ? formatMessage({id: 'wishlist.folder.rename_title', defaultMessage: 'Edit collection'})
            : formatMessage({id: 'wishlist.folder.create_title', defaultMessage: 'New Collection'})

    return (
        <WebDrawer
            isOpen={isOpen}
            onClose={handleCancel}
            title={drawerTitle}
            size="full"
        >
            {view === 'edit' ? (
                <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                    <Stack spacing={6}>
                        {/* Name field — webFloatingField (Text Input — Required) */}
                        <Field
                            name="name"
                            label={formatMessage({id: 'wishlist.folder.name_label', defaultMessage: 'Collection Name'})}
                            variant="webFloatingField"
                            control={control}
                            inputProps={{maxLength: 18}}
                            rules={{
                                required: formatMessage({
                                    id: 'wishlist.folder.name_required',
                                    defaultMessage: 'Please Enter a Name'
                                })
                            }}
                            error={errors.name}
                        />

                        {/* Collection visibility — create mode only */}
                        {!isRename && (
                            <Stack spacing={3}>
                                <Text textStyle="bodySmBold">
                                    <FormattedMessage
                                        id="wishlist.folder.visibility_title"
                                        defaultMessage="Collection visibility"
                                    />
                                </Text>
                                <Flex align="flex-start" gap={3}>
                                    <Switch
                                        isChecked={isPublic}
                                        onChange={(e) => setIsPublic(e.target.checked)}
                                        size="md"
                                        sx={styles.visibilitySwitch}
                                    />
                                    <Text textStyle="bodySmRegular" sx={styles.visibilityText}>
                                        {isPublic ? (
                                            <FormattedMessage
                                                id="wishlist.folder.visibility_public"
                                                defaultMessage="Your folder can be seen and shared with others."
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="wishlist.folder.visibility_private"
                                                defaultMessage="Your folder is private, toggle ON to be able to share with others"
                                            />
                                        )}
                                    </Text>
                                </Flex>
                            </Stack>
                        )}

                        {/* Action buttons — stacked, full width, inside content */}
                        <Stack spacing={3} pt={2}>
                            <Button
                                type="submit"
                                variant="webPrimary"
                                w="100%"
                                isLoading={isSubmitting}
                            >
                                <FormattedMessage id="wishlist.folder.rename_submit" defaultMessage="Save" />
                            </Button>
                            <Button
                                type="button"
                                variant="webSecondaryOutline"
                                w="100%"
                                onClick={handleCancel}
                            >
                                <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                            </Button>
                        </Stack>

                        {/* Remove collection — edit mode only */}
                        {isRename && (
                            <Stack spacing={0} align="center">
                                <Button
                                    type="button"
                                    variant="webTertiary"
                                    sx={styles.removeCollectionButton}
                                    ref={removeCollectionButtonRef}
                                    onClick={() => setView('confirm')}
                                >
                                    <Text textStyle="bodySmLink">
                                        <FormattedMessage
                                            id="wishlist.folder.remove_btn"
                                            defaultMessage="Remove this collection"
                                        />
                                    </Text>
                                </Button>
                                <Text textStyle="bodySmRegular" color="webTypo.textTertiary" textAlign="center">
                                    <FormattedMessage
                                        id="wishlist.folder.remove_warning"
                                        defaultMessage="This permanently deletes the collection."
                                    />
                                </Text>
                            </Stack>
                        )}
                    </Stack>
                </form>
            ) : (
                /* ── Confirmation view ── */
                <Stack spacing={6}>
                    {/* Dynamic description */}
                    <Text textStyle="bodySmRegular">
                        {folderItemCount > 0 ? (
                            <>
                                <Text as="span" textStyle="bodySmBold">
                                    &ldquo;{folder?.name}&rdquo;
                                </Text>
                                {' '}
                                <FormattedMessage
                                    id="wishlist.folder.remove_description_items"
                                    defaultMessage="has {count, plural, one {# item} other {# items}}. What should happen to them when you remove this collection?"
                                    values={{count: folderItemCount}}
                                />
                            </>
                        ) : (
                            <>
                                <FormattedMessage
                                    id="wishlist.folder.remove_description_empty_prefix"
                                    defaultMessage="Remove"
                                />
                                {' '}
                                <Text as="span" textStyle="bodySmBold">
                                    &ldquo;{folder?.name}&rdquo;
                                </Text>
                                {'? '}
                                <FormattedMessage
                                    id="wishlist.folder.remove_description_empty_suffix"
                                    defaultMessage="This action cannot be undone."
                                />
                            </>
                        )}
                    </Text>

                    {/* Radio options */}
                    {folderItemCount > 0 && (
                        <RadioGroup value={deleteMode} onChange={setDeleteMode}>
                            <Stack spacing={4}>
                                <Radio value="keep">
                                    <Stack spacing={1} ml={2}>
                                        <Text textStyle="bodySmBold">
                                            <FormattedMessage
                                                id="wishlist.folder.remove_option_keep_title"
                                                defaultMessage="Keep items"
                                            />
                                        </Text>
                                        <Text textStyle="bodySmRegular" color="webTypo.textSecondary">
                                            <FormattedMessage
                                                id="wishlist.folder.remove_option_keep_desc_before"
                                                defaultMessage="The collection will be removed but the items will stay in"
                                            />
                                            {' '}
                                            <Text as="span" textStyle="bodySmBold" color="webTypo.textSecondary">
                                                &ldquo;<FormattedMessage
                                                    id="wishlist.folder.my_favourites"
                                                    defaultMessage="My Favorites"
                                                />&rdquo;
                                            </Text>
                                        </Text>
                                    </Stack>
                                </Radio>
                                <Radio value="remove">
                                    <Stack spacing={1} ml={2}>
                                        <Text textStyle="bodySmBold">
                                            <FormattedMessage
                                                id="wishlist.folder.remove_option_remove_title"
                                                defaultMessage="Remove collection and items"
                                            />
                                        </Text>
                                        <Text textStyle="bodySmRegular" color="webTypo.textSecondary">
                                            <FormattedMessage
                                                id="wishlist.folder.remove_option_remove_desc"
                                                defaultMessage="The collection and its {count, plural, one {# item} other {# items}} will be permanently removed"
                                                values={{count: folderItemCount}}
                                            />
                                        </Text>
                                    </Stack>
                                </Radio>
                            </Stack>
                        </RadioGroup>
                    )}

                    {/* Action buttons */}
                    <Stack spacing={3} pt={2}>
                        <Button
                            type="button"
                            variant="webPrimary"
                            w="100%"
                            onClick={handleConfirmDelete}
                        >
                            <FormattedMessage id="wishlist.folder.remove_confirm_btn" defaultMessage="Confirm" />
                        </Button>
                        <Button
                            type="button"
                            variant="webSecondaryOutline"
                            w="100%"
                            onClick={handleCancelConfirm}
                        >
                            <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                        </Button>
                    </Stack>
                </Stack>
            )}
        </WebDrawer>
    )
}

WishlistFolderFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    folder: PropTypes.shape({id: PropTypes.string, name: PropTypes.string}),
    folderItemCount: PropTypes.number
}

export default WishlistFolderFormModal
